import { CartGroup, CartTableContent, CartTableItem, GutteringSystemData, LocalCart, Product, ProductAddition, ProductInCartTable, ProductInLocalCart, Unit } from 'lib/types';
import _ from 'lodash';
import store from 'store';

export const getTotalQuantityInSelectedUnit = (productInLocalCart: ProductInLocalCart): number => +(productInLocalCart.quantity * (productInLocalCart.product.isDimension ? (productInLocalCart.dimension ?? 1) : 1));

export const getTotalQuantityInDefaultUnit = (productInLocalCart: ProductInLocalCart): number => +(getTotalQuantityInSelectedUnit(productInLocalCart) * (productInLocalCart.unit?.converter ?? 1));

export const getTotalQuantityInM2 = (productInLocalCart: ProductInLocalCart): number => +(getTotalQuantityInDefaultUnit(productInLocalCart) * productInLocalCart.product.converterM2);

export const getProductPriceBeforeDiscountPerDefaultUnit = (productInLocalCart: ProductInLocalCart): number => {
    const additionsPrice = _.reduce(
        productInLocalCart.additionsSelected,
        (sum, isSelected, index) => (
            isSelected ? sum + productInLocalCart.product.additions[index].price : sum
        ),
        0
    );
    return productInLocalCart.product.priceInfo.price + additionsPrice;
};

export const getProductDiscountPerDefaultUnit = (productInLocalCart: ProductInLocalCart): number => (
    getProductPriceBeforeDiscountPerDefaultUnit(productInLocalCart) * (productInLocalCart.product.priceInfo.discount / 100)
);

export const getProductPriceAfterDiscountPerDefaultUnit = (productInLocalCart: ProductInLocalCart): number => (
    getProductPriceBeforeDiscountPerDefaultUnit(productInLocalCart) - getProductDiscountPerDefaultUnit(productInLocalCart)
);

export const getTotalPriceBeforeDiscount = (productInLocalCart: ProductInLocalCart): number => (
    getProductPriceBeforeDiscountPerDefaultUnit(productInLocalCart) * getTotalQuantityInDefaultUnit(productInLocalCart)
);

export const getTotalPriceNet = (productInLocalCart: ProductInLocalCart): number => (
    getProductPriceAfterDiscountPerDefaultUnit(productInLocalCart) * getTotalQuantityInDefaultUnit(productInLocalCart)
);

export const getOneProductUnitNet = (productInLocalCart: ProductInLocalCart): number => getProductPriceAfterDiscountPerDefaultUnit(productInLocalCart) * productInLocalCart.unit.converter;

export const getTotalVat = (productInLocalCart: ProductInLocalCart): number => (
    getTotalPriceNet(productInLocalCart) * (productInLocalCart.product.priceInfo.vatRate / 100)
);

export const getTotalPriceGross = (productInLocalCart: ProductInLocalCart): number => getTotalPriceNet(productInLocalCart) + getTotalVat(productInLocalCart);

export const getTotalDiscount = (productInLocalCart: ProductInLocalCart): number => (
    getProductDiscountPerDefaultUnit(productInLocalCart) * getTotalQuantityInDefaultUnit(productInLocalCart)
);

export const getDefaultUnitOfProduct = (product: Product): Unit => {
    let unit = product.priceInfo.units.find((u) => u.isDefault);
    if (unit === undefined) {
        unit = {
            unit: product.unit,
            isDefault: true,
            converter: 1,
            description: product.unit
        };
    }

    return unit;
};

export const mapProductInLocalCartToProductInCartTable = (prd: ProductInLocalCart, indexInCart: number): ProductInCartTable => ({
    ...prd,
    indexInCart,
    quantityJm: getTotalQuantityInSelectedUnit(prd),
    quantityM2: getTotalQuantityInM2(prd),
    price: getTotalPriceGross(prd)
} as ProductInCartTable);

const createCartTableItem = () => ({
    title: '',
    identifier: '',
    products: [] as ProductInCartTable[],
    totalPrice: 0,
    totalNetto: 0,
    totalVat: 0,
    totalQuantity: 0,
    totalQuantityJM: 0,
    totalQuantityM2: 0,
    additionalItemsSummary: {
        quantity: 0,
        netto: 0,
        vat: 0,
        gross: 0
    }
} as CartTableItem);

export const getLocalCartSummaryPrice = (localCart: LocalCart) => _.reduce(localCart.productsInLocalCart, (sum: number, prd: ProductInLocalCart) => sum + getTotalPriceGross(prd), 0);

export const getLocalCartSummaryDiscount = (localCart: LocalCart) => _.reduce(localCart.productsInLocalCart, (sum: number, prd: ProductInLocalCart) => sum + getTotalDiscount(prd), 0);

export const getLocalCartSummaryPriceBeforeDiscount = (localCart: LocalCart) => _.reduce(localCart.productsInLocalCart, (sum: number, prd: ProductInLocalCart) => sum + getTotalPriceBeforeDiscount(prd), 0);

export const getLocalCartSummaryVat = (localCart: LocalCart) => _.reduce(localCart.productsInLocalCart, (sum: number, prd: ProductInLocalCart) => sum + getTotalVat(prd), 0);

export const mapLocalCartToCartTableContent = (localCart: LocalCart) => {
    const newContent: CartTableContent = {
        mainItems: {} as { [key: string]: CartTableItem },
        additionalItems: {} as { [key: string]: { [key: string]: CartTableItem } },
        isodItems: {} as { [key: string]: CartTableItem },
        gutteringsItems: {} as { [key: string]: CartTableItem },
        itemsCount: localCart.productsInLocalCart.length,
        summaryPrice: getLocalCartSummaryPrice(localCart),
        summaryDiscount: getLocalCartSummaryDiscount(localCart),
        summaryPriceBeforeDiscount: getLocalCartSummaryPriceBeforeDiscount(localCart),
        summaryVat: getLocalCartSummaryVat(localCart)
    };

    _.map(localCart.productsInLocalCart, (prd, index) => {
        const productInCartTable: ProductInCartTable = mapProductInLocalCartToProductInCartTable(prd, index);

        let cartTableItem: CartTableItem;
        let identifier: string;
        let title: string;
        if (prd.cartGroup === CartGroup.main && !prd.mainProductSymkar) {
            identifier = prd.product.symKar;
            title = prd.product.description;
            cartTableItem = newContent.mainItems[prd.product.symKar];
            if (!cartTableItem) {
                cartTableItem = createCartTableItem();
                newContent.mainItems[prd.product.symKar] = cartTableItem;
            }
        } else if (prd.cartGroup === CartGroup.main && prd.mainProductSymkar) {
            identifier = `${prd.mainProductSymkar}${prd.product.symKar}`;
            title = prd.product.description;
            cartTableItem = newContent.additionalItems[prd.mainProductSymkar]?.[prd.product.symKar];
            if (!cartTableItem) {
                if (!newContent.additionalItems[prd.mainProductSymkar]) {
                    newContent.additionalItems[prd.mainProductSymkar] = {};
                }
                cartTableItem = createCartTableItem();
                newContent.additionalItems[prd.mainProductSymkar][prd.product.symKar] = cartTableItem;
            }
            if (!newContent.mainItems[prd.mainProductSymkar]) {
                newContent.mainItems[prd.mainProductSymkar] = createCartTableItem();
                newContent.mainItems[prd.mainProductSymkar].identifier = prd.mainProductSymkar;

                const mainProductInfo = _.find(store.getState().data.orders.groupsAndProducts.products, (sp) => sp.symKar === prd.mainProductSymkar);
                newContent.mainItems[prd.mainProductSymkar].title = mainProductInfo?.description ?? _.find(store.getState().data.orders.groupsAndProducts.productsInGroup, (sp) => sp.symKar === prd.mainProductSymkar)?.description ?? '';
            }
            newContent.mainItems[prd.mainProductSymkar].additionalItemsSummary.quantity += prd.quantity;
            newContent.mainItems[prd.mainProductSymkar].additionalItemsSummary.netto += getTotalPriceNet(prd);
            newContent.mainItems[prd.mainProductSymkar].additionalItemsSummary.vat += getTotalVat(prd);
            newContent.mainItems[prd.mainProductSymkar].additionalItemsSummary.gross += productInCartTable.price;
        } else if (prd.cartGroup === CartGroup.isod) {
            identifier = `ISOD${prd.product.symKar}`;
            title = prd.product.description;
            cartTableItem = newContent.isodItems[prd.product.symKar];
            if (!cartTableItem) {
                cartTableItem = createCartTableItem();
                newContent.isodItems[prd.product.symKar] = cartTableItem;
            }
        } else if (prd.cartGroup === CartGroup.gutterings) {
            const systemData = prd.systemData! as GutteringSystemData;
            identifier = JSON.stringify(systemData);
            title = `${systemData.gutterShape} ${systemData.grille} ${systemData.tubeShape} ${systemData.material} ${systemData.size} ${systemData.colorName}`;
            cartTableItem = newContent.gutteringsItems[identifier];
            if (!cartTableItem) {
                cartTableItem = createCartTableItem();
                newContent.gutteringsItems[identifier] = cartTableItem;
            }
        }

        cartTableItem!.title = title!;
        cartTableItem!.identifier = identifier!;
        cartTableItem!.products.push(productInCartTable);
        cartTableItem!.totalPrice += productInCartTable.price;
        cartTableItem!.totalNetto += getTotalPriceNet(prd);
        cartTableItem!.totalVat += getTotalVat(prd);
        cartTableItem!.totalQuantity += productInCartTable.quantity;
        cartTableItem!.totalQuantityJM += productInCartTable.quantityJm;
        cartTableItem!.totalQuantityM2 += productInCartTable.quantityM2;
    });

    return newContent;
};

export const getUnitOrDefault = (product: ProductInLocalCart): Unit => product.unit ?? getDefaultUnitOfProduct(product.product);

export const getUnitShort = (unit: Unit): string => {
    if (unit.description === 'Opakowanie') {
        return 'opk.';
    }

    return unit.unit;
};

export const getUnitLabel = (unit: Unit, baseUnit: string): string => {
    if (unit.converter === 1.0) {
        return unit.description;
    }

    return `${unit.description} (${unit.converter}\xa0${baseUnit}.)`;
};

export const totalGutterPrice = () => _.reduce(store.getState().data.orders.localCart.productsInLocalCart, (sum, prd) => sum + ((prd.cartGroup === CartGroup.gutterings) ? getTotalPriceGross(prd) : 0), 0);
export const totalGutterNetto = () => _.reduce(store.getState().data.orders.localCart.productsInLocalCart, (sum, prd) => sum + ((prd.cartGroup === CartGroup.gutterings) ? getTotalPriceNet(prd) : 0), 0);
export const totalGutterVat = () => _.reduce(store.getState().data.orders.localCart.productsInLocalCart, (sum, prd) => sum + ((prd.cartGroup === CartGroup.gutterings) ? getTotalVat(prd) : 0), 0);

export const totalIsodPrice = () => _.reduce(store.getState().data.orders.localCart.productsInLocalCart, (sum, prd) => sum + ((prd.cartGroup === CartGroup.isod) ? getTotalPriceGross(prd) : 0), 0);
export const totalIsodNetto = () => _.reduce(store.getState().data.orders.localCart.productsInLocalCart, (sum, prd) => sum + ((prd.cartGroup === CartGroup.isod) ? getTotalPriceNet(prd) : 0), 0);
export const totalIsodVat = () => _.reduce(store.getState().data.orders.localCart.productsInLocalCart, (sum, prd) => sum + ((prd.cartGroup === CartGroup.isod) ? getTotalVat(prd) : 0), 0);

export const getAllAdditionsInLocalCart = () => {
    let additions: ProductAddition[] = [];
    _.forEach(store.getState().data.orders.localCart.productsInLocalCart, (prd) => {
        additions = _.unionBy(additions, prd.product.additions, 'name');
    });
    return additions;
};

export const getPriceAfterDiscountPerSquaredMeter = (productInLocalCart: ProductInLocalCart) => getProductPriceAfterDiscountPerDefaultUnit(productInLocalCart) / productInLocalCart.product.converterM2;

export const getProductPriceData = (productInCartData: ProductInLocalCart) => {
    const { discount } = productInCartData.product.priceInfo;
    return {
        discount,
        priceAfterDiscountPerDefaultUnit: getProductPriceAfterDiscountPerDefaultUnit(productInCartData),
        priceAfterDiscountPerSquaredMeter: getPriceAfterDiscountPerSquaredMeter(productInCartData),
        net: getTotalPriceNet(productInCartData),
        vat: getTotalVat(productInCartData),
        gross: getTotalPriceGross(productInCartData)
    };
};
